import type { CountryConfig } from '@ctct/domain-layer';

import type { ContentStrategy } from './ContentStrategy';
import {
  InternationalPricingContentStrategy,
  type InternationalCountryCode,
} from './InternationalPricingContentStrategy/InternationalPricingContentStrategy';
import { PlanRestructureContentStrategy } from './PlanRestructureContentStrategy/PlanRestructureContentStrategy';
import { PricingContentStrategy } from './PricingContentStrategy';
import { ValueFocusedMWebContentStrategy } from './ValueFocusedMWebContentStrategy/ValueFocusedMWebContentStrategy';
import type { Flags } from '../types';

const SMS_INTERNATIONAL_COUNTRY_CODES = new Set<InternationalCountryCode>(['AU', 'GB']);

const isInternationalCountryCode = (countryCode: string): countryCode is InternationalCountryCode =>
  SMS_INTERNATIONAL_COUNTRY_CODES.has(countryCode as InternationalCountryCode);

/**
 * Selects the Plans & Pricing comparison table content strategy.
 *
 * When SMS-eligible and plan-group country-config resolves to AU or GB, use the
 * international layout (AU injects Data & Compliance; GB does not). Otherwise, use
 * the mWeb value-focused variant when that flag is set, or the default US layout.
 *
 * See specs/sms-international.md.
 */
export function createPlanComparisonContentStrategy(
  flags: Flags,
  planGroupCountryConfig?: CountryConfig
): ContentStrategy {
  if (flags.isSmsEligible && planGroupCountryConfig) {
    const { countryCode } = planGroupCountryConfig;

    if (isInternationalCountryCode(countryCode)) {
      return new InternationalPricingContentStrategy(countryCode);
    }
  }

  // The plan-restructure cohort takes precedence over the value-focused mWeb variant
  // so trialers assigned to planGroupId 385/386 see the repackaged copy on both desktop
  // and mWeb. See CPD-25373 / specs/characterizations/plans-and-pricing-display.md.
  if (flags.isPlanRestructureExperiment) {
    return new PlanRestructureContentStrategy();
  }

  if (flags.isValueFocusedMWebVariant) {
    return new ValueFocusedMWebContentStrategy();
  }

  return new PricingContentStrategy();
}
